import { useState } from 'react';
import { Check, ChevronDown, ChevronRight, Copy } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { DebugSection } from '../DebugSection';
import { sanitizeDebugPayload, summarizeDebugPayload } from '../sanitize';
import { useDebugCapability } from '../useDebugCapability';
import type {
  DebugLayerEntry,
  DebugSection as DebugSectionId,
} from '../types';

// Häufige technische IDs, die Operatoren beim Debuggen aus dem Payload
// kopieren möchten. Bewusst eng gehalten — keine PII, keine Tokens.
const COPYABLE_KEYS = [
  'importId',
  'correlationId',
  'errorCode',
] as const;

interface Props {
  title: string;
  description?: string;
  entries: DebugLayerEntry[];
  section: DebugSectionId;
}

/**
 * Generische Liste für Debug-Entries.
 *
 * Payload-Rendering folgt strikt der `payloadVisibility`-Klassifizierung:
 * - `hidden`     → Payload wird nie angezeigt
 * - `redacted`   → nur eine redaktierte Top-Level-Zusammenfassung
 * - `admin_safe` → vollständig (sanitisiert) nur für berechtigte Admins
 *
 * Sanitisierung entfernt zusätzlich offensichtliche Token-/Secret-Keys.
 */
export function EntriesSection({ title, description, entries, section }: Props) {
  const filtered = entries.filter((e) => e.section === section);
  if (filtered.length === 0) return null;
  return (
    <DebugSection title={title} description={description} visible>
      {filtered.map((e) => (
        <EntryRow key={e.id} entry={e} />
      ))}
    </DebugSection>
  );
}

function EntryRow({ entry }: { entry: DebugLayerEntry }) {
  const { canSeeAdminPayload } = useDebugCapability();
  const [openPayload, setOpenPayload] = useState(false);

  const visibility = entry.payloadVisibility ?? 'hidden';
  const canRenderPayload =
    entry.payload !== undefined &&
    ((visibility === 'admin_safe' && canSeeAdminPayload) ||
      visibility === 'redacted');

  // Copy-Buttons nur für vollständig sichtbare admin_safe Payloads.
  // hidden/redacted bleiben strikt aussen vor.
  const copyableEntries =
    entry.payload &&
    visibility === 'admin_safe' &&
    canSeeAdminPayload &&
    typeof entry.payload === 'object' &&
    !Array.isArray(entry.payload)
      ? COPYABLE_KEYS.flatMap((k) => {
          const v = (entry.payload as Record<string, unknown>)[k];
          return typeof v === 'string' && v.length > 0
            ? [{ key: k, value: v }]
            : [];
        })
      : [];

  return (
    <div className="rounded-md border border-border/60 bg-muted/30 px-3 py-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-medium leading-snug">{entry.title}</p>
          {entry.description ? (
            <p className="text-xs text-muted-foreground mt-0.5 break-words">
              {entry.description}
            </p>
          ) : null}
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground/70 mt-1">
            {entry.scope}
            {entry.route ? ` · ${entry.route}` : ''}
          </p>
        </div>
        {entry.severity ? <SeverityBadge severity={entry.severity} /> : null}
      </div>

      {canRenderPayload ? (
        <div className="mt-2">
          {copyableEntries.length > 0 ? (
            <div className="mb-1.5 flex flex-wrap gap-1">
              {copyableEntries.map((c) => (
                <CopyChip key={c.key} label={c.key} value={c.value} />
              ))}
            </div>
          ) : null}
          <button
            type="button"
            onClick={() => setOpenPayload((v) => !v)}
            className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
          >
            {openPayload ? (
              <ChevronDown className="h-3 w-3" />
            ) : (
              <ChevronRight className="h-3 w-3" />
            )}
            Details
          </button>
          {openPayload ? (
            <pre
              data-testid={`debug-entry-payload-${entry.id}`}
              className="mt-1 max-h-48 overflow-auto rounded bg-background/70 border border-border/40 p-2 text-[11px] leading-snug whitespace-pre-wrap break-words"
            >
              {visibility === 'redacted'
                ? summarizeDebugPayload(entry.payload)
                : safeStringify(sanitizeDebugPayload(entry.payload))}
            </pre>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function SeverityBadge({ severity }: { severity: 'info' | 'warning' | 'error' }) {
  const tone =
    severity === 'error'
      ? 'destructive'
      : severity === 'warning'
        ? 'secondary'
        : 'outline';
  return (
    <Badge variant={tone as 'destructive' | 'secondary' | 'outline'} className="shrink-0 capitalize text-[10px]">
      {severity}
    </Badge>
  );
}

function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function CopyChip({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      data-testid={`debug-copy-${label}`}
      onClick={async () => {
        try {
          if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(value);
          }
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1200);
        } catch {
          // Clipboard kann z. B. in iframes blockiert sein — bewusst
          // still scheitern, Operator sieht den Wert dann im Payload.
        }
      }}
      className="inline-flex items-center gap-1 rounded border border-border/60 bg-background/60 px-1.5 py-0.5 text-[10px] font-mono text-muted-foreground hover:text-foreground hover:border-border transition-colors"
      title={`${label} kopieren`}
    >
      {copied ? (
        <Check className="h-3 w-3 text-emerald-500" aria-hidden />
      ) : (
        <Copy className="h-3 w-3" aria-hidden />
      )}
      <span>{label}</span>
    </button>
  );
}
